<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <span>{{ t.addresses?.title || 'Delivery Addresses' }}</span>
      <v-btn color="success" size="small" @click="openAddDialog">
        <v-icon class="mr-1">mdi-plus</v-icon>
        {{ t.addresses?.add || 'Add Address' }}
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3"></v-progress-linear>

      <v-data-table
        :headers="tableHeaders"
        :items="addresses"
        :loading="loading"
        density="compact"
        class="elevation-0"
      >
        <template v-slot:[`item.location_name`]="{ item }">
          <span class="font-weight-medium">{{ item.location_name }}</span>
        </template>

        <template v-slot:[`item.address`]="{ item }">
          <span class="text-caption">{{ item.full_address }}</span>
        </template>

        <template v-slot:[`item.contact`]="{ item }">
          <div v-if="item.contact_name">{{ item.contact_name }}</div>
          <div v-if="item.contact_phone" class="text-caption">{{ item.contact_phone }}</div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn icon size="small" variant="text" @click="editAddress(item)" :title="t.common?.edit || 'Edit'">
            <v-icon size="small">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon size="small" variant="text" color="error" @click="confirmDelete(item)" :title="t.common?.delete || 'Delete'">
            <v-icon size="small">mdi-delete</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="text-center pa-4 text-grey">
            {{ t.addresses?.noAddresses || 'No delivery addresses saved' }}
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>

  <!-- Add/Edit Dialog -->
  <v-dialog v-model="showDialog" max-width="700px" persistent>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ editingAddress ? (t.addresses?.edit || 'Edit Address') : (t.addresses?.add || 'Add Address') }}</span>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="formData.location_name"
              :label="t.delivery?.locationName || 'Location Name'"
              variant="outlined"
              density="compact"
              :rules="[v => !!v || (t.validation?.required || 'Required')]"
              placeholder="Office, Home, Warehouse..."
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="formData.contact_name"
              :label="t.delivery?.contactName || 'Contact Name'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="formData.contact_phone"
              :label="t.delivery?.contactPhone || 'Phone'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="formData.contact_email"
              :label="t.delivery?.contactEmail || 'Email'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <v-row>
          <v-col cols="8">
            <v-text-field
              v-model="formData.street"
              :label="t.delivery?.street || 'Street'"
              variant="outlined"
              density="compact"
              :rules="[v => !!v || (t.validation?.required || 'Required')]"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="formData.street_number"
              :label="t.delivery?.streetNumber || 'Number'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="3">
            <v-text-field
              v-model="formData.building"
              :label="t.delivery?.building || 'Building'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="formData.entrance"
              :label="t.delivery?.entrance || 'Entrance'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="formData.floor"
              :label="t.delivery?.floor || 'Floor'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="formData.apartment"
              :label="t.delivery?.apartment || 'Apartment'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model="formData.intercom"
              :label="t.delivery?.intercom || 'Intercom'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="formData.sector"
              :label="t.delivery?.sector || 'Sector'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="formData.postal_code"
              :label="t.delivery?.postalCode || 'Postal Code'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model="formData.city"
              :label="t.delivery?.city || 'City'"
              variant="outlined"
              density="compact"
              :rules="[v => !!v || (t.validation?.required || 'Required')]"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="formData.county"
              :label="t.delivery?.county || 'County'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="formData.country"
              :label="t.delivery?.country || 'Country'"
              variant="outlined"
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="formData.other_details"
              :label="t.delivery?.otherDetails || 'Other Details'"
              variant="outlined"
              density="compact"
              rows="2"
              placeholder="Instructions for courier..."
            ></v-textarea>
          </v-col>
        </v-row>

        <v-alert v-if="formError" type="error" density="compact" class="mt-3">
          {{ formError }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn variant="outlined" @click="closeDialog" :disabled="saving">
          {{ t.common?.cancel || 'Cancel' }}
        </v-btn>
        <v-btn color="primary" @click="saveAddress" :loading="saving" :disabled="!isFormValid">
          {{ t.common?.save || 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showDeleteDialog" max-width="400px">
    <v-card>
      <v-card-title>{{ t.common?.confirm || 'Confirm' }}</v-card-title>
      <v-card-text>
        {{ t.addresses?.confirmDelete || 'Are you sure you want to delete this address?' }}
        <div v-if="addressToDelete" class="mt-2 font-weight-medium">
          {{ addressToDelete.location_name }} - {{ addressToDelete.city }}
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="outlined" @click="showDeleteDialog = false">
          {{ t.common?.cancel || 'Cancel' }}
        </v-btn>
        <v-btn color="error" @click="deleteAddress" :loading="deleting">
          {{ t.common?.delete || 'Delete' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getClientDeliveryAddresses, saveClientDeliveryAddress, deleteClientDeliveryAddress } from "@/composables/useClientDeliveryAddressAPI";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'ClientDeliveryAddresses',
  props: {
    clientId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['addressesUpdated'],
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      addresses: [],
      showDialog: false,
      showDeleteDialog: false,
      editingAddress: null,
      addressToDelete: null,
      formError: null,
      formData: this.getEmptyFormData()
    };
  },
  computed: {
    t() {
      return tRef.value;
    },
    token() {
      return store.getters.getToken;
    },
    tableHeaders() {
      return [
        { title: this.t.delivery?.locationName || 'Location', key: 'location_name', width: '150px' },
        { title: this.t.delivery?.address || 'Address', key: 'address' },
        { title: this.t.delivery?.contactName || 'Contact', key: 'contact', width: '150px' },
        { title: this.t.common?.actions || 'Actions', key: 'actions', width: '100px', sortable: false }
      ];
    },
    isFormValid() {
      return this.formData.location_name && this.formData.street && this.formData.city;
    }
  },
  methods: {
    getEmptyFormData() {
      return {
        id: null,
        location_name: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        street: '',
        street_number: '',
        building: '',
        entrance: '',
        floor: '',
        apartment: '',
        intercom: '',
        sector: '',
        postal_code: '',
        city: '',
        county: '',
        country: 'Romania',
        other_details: ''
      };
    },

    async loadAddresses() {
      this.loading = true;
      try {
        const response = await getClientDeliveryAddresses(this.token, this.clientId);
        if (!response.error) {
          this.addresses = response.addresses || [];
          log("Loaded addresses:");
          log(this.addresses);
        } else {
          log("Error loading addresses: " + response.message);
        }
      } catch (err) {
        log("Exception loading addresses:");
        log(err);
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.editingAddress = null;
      this.formData = this.getEmptyFormData();
      this.formError = null;
      this.showDialog = true;
    },

    editAddress(address) {
      this.editingAddress = address;
      this.formData = { ...address };
      this.formError = null;
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.editingAddress = null;
      this.formData = this.getEmptyFormData();
      this.formError = null;
    },

    async saveAddress() {
      if (!this.isFormValid) {
        this.formError = this.t.validation?.required || 'Please fill required fields';
        return;
      }

      this.saving = true;
      this.formError = null;

      try {
        const data = {
          ...this.formData,
          client_id: this.clientId
        };

        const response = await saveClientDeliveryAddress(this.token, data);

        if (!response.error) {
          await this.loadAddresses();
          this.$emit('addressesUpdated');
          this.closeDialog();
        } else {
          this.formError = response.message || 'Error saving address';
        }
      } catch (err) {
        log("Exception saving address:");
        log(err);
        this.formError = 'Error saving address';
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(address) {
      this.addressToDelete = address;
      this.showDeleteDialog = true;
    },

    async deleteAddress() {
      if (!this.addressToDelete) return;

      this.deleting = true;
      try {
        const response = await deleteClientDeliveryAddress(this.token, this.addressToDelete.id, this.clientId);

        if (!response.error) {
          await this.loadAddresses();
          this.$emit('addressesUpdated');
          this.showDeleteDialog = false;
          this.addressToDelete = null;
        } else {
          log("Error deleting address: " + response.message);
        }
      } catch (err) {
        log("Exception deleting address:");
        log(err);
      } finally {
        this.deleting = false;
      }
    }
  },

  mounted() {
    this.loadAddresses();
  },

  watch: {
    clientId() {
      this.loadAddresses();
    }
  }
});
</script>

<style scoped>
.v-data-table :deep(td) {
  padding: 8px 12px !important;
}
</style>
